import React, { useEffect, useId, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import { getPharmacistStats } from "../../services/pharmacistProfileService";
import { CnCOrderStatus } from "../../types/api";
import { PharmacistStatsResponse } from "../../types/pharmacistProfile.types";
import styles from "./PharmacistProfilePage.module.scss";

// ITAM-2674: the adviser's consult and reservation counts. Read-only, one
// reader, local state. The response is rendered as received: the backend owns
// the numbers (and the total), the frontend owns failure, cancellation and
// supersession only.

export const CNC_ORDER_STATUS_LABELS: Record<CnCOrderStatus, string> = {
  CREATED: "Kreirana",
  PREPARED: "Spremna",
  SEEN: "Viđena",
  PROCESSED: "Obrađena",
  CANCELED: "Otkazana",
  REJECTED: "Odbijena",
  TAKEN: "Preuzeta",
  NOT_TAKEN: "Nije preuzeta",
  FOR_DELIVERY: "Za predaju",
  IN_DELIVERY: "Preuzelo ovlašćeno lice",
  SUSPENDED: "Obustavljena",
};

export interface ReservationGroup {
  key: string;
  label: string;
  /** Rendered in this order, so each list is kept in backend enum order. */
  statuses: readonly CnCOrderStatus[];
}

/**
 * Presentation groups. Tests prove they partition `CNC_ORDER_STATUSES`
 * exactly and that each list follows enum order; the tuple's own type
 * assertion proves it covers `CnCOrderStatus`.
 */
export const RESERVATION_GROUPS: readonly ReservationGroup[] = [
  { key: "done", label: "Preuzete", statuses: ["TAKEN"] },
  {
    key: "open",
    label: "U toku",
    statuses: [
      "CREATED",
      "PREPARED",
      "SEEN",
      "PROCESSED",
      "FOR_DELIVERY",
      "IN_DELIVERY",
    ],
  },
  {
    key: "failed",
    label: "Nerealizovane",
    statuses: ["CANCELED", "REJECTED", "NOT_TAKEN", "SUSPENDED"],
  },
];

type StatsPhase =
  | { phase: "loading" }
  | { phase: "loaded"; stats: PharmacistStatsResponse }
  | { phase: "error" };

interface Props {
  userId: number;
}

const formatCount = (value: number): string => value.toLocaleString("sr-RS");

const PharmacistStatsPanel: React.FC<Props> = ({ userId }) => {
  const headingId = useId();
  const [state, setState] = useState<StatsPhase>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ phase: "loading" });

    getPharmacistStats(userId, controller.signal).then(
      (stats) => {
        if (controller.signal.aborted) return;
        setState({ phase: "loaded", stats });
      },
      () => {
        // Cancellation only ever comes from this cleanup, so a CanceledError
        // always arrives aborted and never reaches the error state.
        if (controller.signal.aborted) return;
        setState({ phase: "error" });
      }
    );

    return () => controller.abort();
  }, [userId, attempt]);

  const retry = () => setAttempt((n) => n + 1);

  const renderBody = () => {
    if (state.phase === "loading") {
      return <p className={styles.hint}>Učitavanje statistike...</p>;
    }
    if (state.phase === "error") {
      return (
        <>
          <p className={styles.error} role="alert">
            Statistika trenutno nije dostupna.
          </p>
          <Button type="button" buttonType="white" onClick={retry}>
            Pokušaj ponovo
          </Button>
        </>
      );
    }

    const { stats } = state;
    return (
      <>
        <div className={styles.statsGrid}>
          <div className={styles.statTile}>
            <span className={styles.statValue}>
              {formatCount(stats.reportsCount)}
            </span>
            <span className={styles.statLabel}>Konsultacije sa izveštajem</span>
          </div>
          <div className={styles.statTile}>
            <span className={styles.statValue}>
              {formatCount(stats.reservationsTotal)}
            </span>
            <span className={styles.statLabel}>
              Rezervacije kreirane tokom konsultacija
            </span>
          </div>
        </div>
        <ul className={styles.statGroups}>
          {RESERVATION_GROUPS.map((group) => {
            let sum = 0;
            const parts: string[] = [];
            group.statuses.forEach((status) => {
              const count = stats.reservationsByStatus[status];
              sum += count;
              if (count !== 0) {
                parts.push(
                  `${CNC_ORDER_STATUS_LABELS[status]} ${formatCount(count)}`
                );
              }
            });
            const detail = parts.join(", ");
            return (
              <li key={group.key} className={styles.statGroup}>
                <span className={styles.statGroupLabel}>{group.label}</span>
                <span className={styles.statValue}>{formatCount(sum)}</span>
                {detail && <span className={styles.hint}>{detail}</span>}
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.cardTitle}>
        Statistika
      </h2>
      {renderBody()}
    </section>
  );
};

export default PharmacistStatsPanel;
